import React from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { UserViewPageProps, UserViewPageParams, isOfTypeTabKey } from './types';

import { FormContextProvider } from './FormContext';
import { AgentUserCalendarContextProvider } from '../ShowingsCalendarPage/AgentUserCalendarContext';
import Page from './Page';

// Grab all of the parameters out of the query URL.
const useQuery = () => new URLSearchParams(useLocation().search);

/**
 * Page types defined by prop/route (all are mutually exclusive)
 *
 * - isEdit: Implied default state. Page for editing a user.
 *
 * - isNew: Page for creating a new user.
 *
 * - isProfile: The account settings or profile page. Allows the admin/agent to update
 * their own account settings.
 *
 * - isIncomingTransfer: Page for viewing/editing an incoming pending/suggested transfer.
 *
 * Page types defined by the user (see FormContext for what these states mean for the user)
 *
 * - isAnsweringOrMlsStaff: Is only true if the user is an answering or mls staff user. Can be true in any
 * of the non-transfer pages. Only the profie tab is visible and only basic contact details are visible/editable.
 *
 * - isDeleted: Can only be true in edit page. Only the profile tab is visible.
 * All the inputs (except avatar) are disabled and the submit button has been removed.
 *
 * - isOutgoingTransfer: Can be true in edit and profile page. Essentially the same as
 * the edit/profile page but there will be an option for the admin
 * (or agent if its the profile page) to accept or deny the transfer.
 *
 */
const UserViewPage: React.FC<UserViewPageProps> = ({
  isNew,
  isProfile,
  isIncomingTransfer,
}) => {
  const { userId, transferId } = useParams<UserViewPageParams>();
  const query = useQuery();
  const tab = query.get('tab');
  const contextProps = {
    isNew,
    isProfile,
    isIncomingTransfer,
    userId,
    transferId,
    tab: isOfTypeTabKey(tab) ? tab : undefined,
  };
  return (
    <FormContextProvider {...contextProps}>
      <AgentUserCalendarContextProvider>
        <Page
          showHistoryModal={query.get('showHistory') === 'true'}
          fromDeletedTab={query.get('from') === 'manage-deleted-staff'}
        />
      </AgentUserCalendarContextProvider>
    </FormContextProvider>
  );
};

export default UserViewPage;
